import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  BookOpen,
  ClipboardList,
  FileText,
  Scale,
  Users,
} from 'lucide-react';

import type { ProjectArtifactType } from '@/api/project-artifacts.types';

/** Sprint 5.2a built-in artifact types (single source of truth for picker + sidebar labels). */
export type BuiltInArtifactTypeId =
  | 'risk_register'
  | 'raid_log'
  | 'lessons_learned'
  | 'decision_log'
  | 'stakeholder_register'
  | 'status_report'
  | 'brd';

export interface ArtifactTypeMeta {
  id: BuiltInArtifactTypeId;
  label: string;
  description: string;
  icon: LucideIcon;
}

/** Locked Pause 1d copy (IP-safe, title case labels). */
export const BUILTIN_ARTIFACT_TYPES: readonly ArtifactTypeMeta[] = [
  {
    id: 'risk_register',
    label: 'Risk Register',
    description: 'Identify potential risks, assess impact, and track mitigation plans.',
    icon: AlertTriangle,
  },
  {
    id: 'raid_log',
    label: 'RAID Log',
    description: 'Track risks, assumptions, issues, and dependencies in one place.',
    icon: ClipboardList,
  },
  {
    id: 'lessons_learned',
    label: 'Lessons Learned',
    description: 'Capture insights and takeaways to improve future outcomes.',
    icon: BookOpen,
  },
  {
    id: 'decision_log',
    label: 'Decision Log',
    description: 'Record key decisions, rationale, and agreed actions.',
    icon: Scale,
  },
  {
    id: 'stakeholder_register',
    label: 'Stakeholder Register',
    description: 'Identify stakeholders, understand needs, and track engagement.',
    icon: Users,
  },
  {
    id: 'status_report',
    label: 'Status Report',
    description: 'Summarize progress, highlights, and next steps.',
    icon: FileText,
  },
  {
    id: 'brd',
    label: 'Business Requirements Document',
    description: 'Capture the business problem, scope, requirements, and sign-off.',
    icon: FileText,
  },
] as const;

/**
 * Types that are no longer offered when creating an artifact. DEC-10 rules
 * `work_risks` the canonical risk store, so a new Risk Register artifact would
 * be a second, ungoverned place to record risks. Existing artifacts of these
 * types still open and keep their label and icon (see `artifactTypeLabel`).
 */
export const RETIRED_ARTIFACT_TYPE_IDS: readonly BuiltInArtifactTypeId[] = [
  'risk_register',
];

/** What the "new artifact" picker offers: the built-ins minus the retired ones. */
export const PICKABLE_ARTIFACT_TYPES: readonly ArtifactTypeMeta[] =
  BUILTIN_ARTIFACT_TYPES.filter((t) => !RETIRED_ARTIFACT_TYPE_IDS.includes(t.id));

export function artifactTypeLabel(type: ProjectArtifactType | string): string {
  const found = BUILTIN_ARTIFACT_TYPES.find((t) => t.id === type);
  return found?.label ?? type.replace(/_/g, ' ');
}

export function artifactTypeIcon(type: ProjectArtifactType | string): LucideIcon {
  const found = BUILTIN_ARTIFACT_TYPES.find((t) => t.id === type);
  return found?.icon ?? FileText;
}
